import { useCallback, useEffect, useState } from "react";
import { SITE_SERVER_URL } from "@/shared/config/player";
import { SPHERE_FULL_AT } from "./otherScores";

/**
 * Баллы за вклад и польза от него.
 *
 * Личность сервер берёт только из подписанного токена сеанса — адрес из
 * localStorage сюда не годится, от него зависят начисления.
 *
 * Две разные величины:
 *  - points — что человек сделал; ИМЕННО ИМИ растёт звание;
 *  - people_reached — сколько людей посмотрело исправленные им фрагменты;
 *    на звание не влияет, показывается рядом и питает огоньки.
 *    Считается ночным проходом на сервере: на лету пришлось бы перебирать
 *    тысячи сеансов просмотра ради одного числа.
 *
 * unseen_points и people_new — то, что набежало, пока человека не было. Главное
 * начисление приходит через дни после отправки, когда его на сайте нет, поэтому
 * показать это надо при следующем заходе, а не молча поменять цифру.
 */
export type ContribEvent = {
    kind: string;
    points: number;
    people?: number;
    at: number;
};

export type ContribScore = {
    points: number;
    corrections_submitted: number;
    corrections_accepted: number;
    people_reached: number;
    /** Куплено продуктов — тоже вклад, и на звание влияет наравне с прочим. */
    purchases: number;
    /** Баллы только за правки, без покупок — для расшифровки в панели. */
    correction_points: number;
    unseen_points: number;
    people_new: number;
    unseen: ContribEvent[];
    signed_in: boolean;
};

const EMPTY: ContribScore = {
    points: 0,
    corrections_submitted: 0,
    corrections_accepted: 0,
    people_reached: 0,
    purchases: 0,
    correction_points: 0,
    unseen_points: 0,
    people_new: 0,
    unseen: [],
    signed_in: false,
};

let cache: ContribScore | null = null;
let inflight: Promise<ContribScore> | null = null;
const listeners = new Set<(s: ContribScore) => void>();

/**
 * Празднование играет ОДИН раз за загрузку страницы. Значок стоит в нескольких
 * местах сразу (шапка, список фрагментов, экран нарезки), и без этого флага
 * анимация запустилась бы у каждого экземпляра.
 */
let celebrationClaimed = false;
export const claimCelebration = (): boolean => {
    if (celebrationClaimed) return false;
    celebrationClaimed = true;
    return true;
};

export const sessionToken = (): string =>
    localStorage.getItem("site_session_token") || "";

export const fetchContribScore = (force = false): Promise<ContribScore> => {
    if (!force && cache) return Promise.resolve(cache);
    if (inflight) return inflight;
    inflight = (async () => {
        const token = sessionToken();
        if (!token) {
            cache = EMPTY;
            return EMPTY;
        }
        try {
            const r = await fetch(
                `${SITE_SERVER_URL}/api/contrib/me?session_token=${encodeURIComponent(token)}`,
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            const next: ContribScore = { ...EMPTY, ...(d?.data || {}) };
            cache = next;
            return next;
        } catch {
            // Счётчик вклада не тот повод, чтобы ломать экран: молча показываем нули.
            return cache || EMPTY;
        } finally {
            inflight = null;
        }
    })();
    return inflight as Promise<ContribScore>;
};

/**
 * Отметить начисления показанными — ПОСЛЕ анимации, а не вместе с чтением.
 * Закрыл вкладку раньше, чем увидел, — начисление дождётся следующего раза.
 */
export const acknowledgeContrib = async (): Promise<void> => {
    const token = sessionToken();
    if (!token) return;
    try {
        await fetch(
            `${SITE_SERVER_URL}/api/contrib/ack?session_token=${encodeURIComponent(token)}`,
            { method: "POST" },
        );
        if (cache) cache = { ...cache, unseen_points: 0, people_new: 0, unseen: [] };
    } catch {
        // Не отметилось — покажем ещё раз в следующий заход. Это не ошибка.
    }
};

/** Позвать после отправки правки — балл появляется сразу, без перезагрузки. */
export const refreshContribScore = async (): Promise<void> => {
    const next = await fetchContribScore(true);
    listeners.forEach((l) => l(next));
};

export const useContribScore = (): ContribScore => {
    const [score, setScore] = useState<ContribScore>(cache ?? EMPTY);
    const onChange = useCallback((s: ContribScore) => setScore(s), []);

    useEffect(() => {
        let alive = true;
        fetchContribScore().then((s) => {
            if (alive) setScore(s);
        });
        listeners.add(onChange);
        return () => {
            alive = false;
            listeners.delete(onChange);
        };
    }, [onChange]);

    return score;
};

/* ---------------------------------------------------------------- звания */

export type Rank = { at: number; name: string };

/**
 * Звание считается по БАЛЛАМ — по всем сразу, откуда бы они ни пришли: ошибки в
 * материалах, покупки, работа в клубе, помощь людям, начисления администраторов.
 * Раньше оно росло по числу людей, и это было неверно: половина способов помочь
 * проекту до зрителя лекции вообще не доходит, а звание за них не поднималось.
 *
 * Отсюда и новые названия. Прежние («Полезен сотням») говорили о размере
 * аудитории, и на входе из баллов стали бы прямой ложью: сотня баллов — это один
 * купленный курс, а не сотня человек. Нынешние говорят о положении в проекте,
 * потому что именно оно и открывает следующие возможности — помощь с ответами,
 * сбор встреч, клуб, административная работа.
 *
 * ⚠ Пороги — предложение, а не утверждённая владельцем шкала. Верхняя ступень
 * посажена ровно на 100000 — тот счёт, при котором владелец велел считать сферу
 * вклада полной, чтобы «сфера налилась» и «выше звания нет» случались вместе, а
 * не в разных местах шкалы. Менять их можно здесь одним списком. Число людей никуда не делось — оно рядом, для мотивации, и
 * питает огоньки внизу файла.
 */
/**
 * Ступени — доли шкалы вклада: верхняя ровно на полной сфере (otherScores.SPHERE_FULL_AT.contrib).
 * Сегодня шкала 100 000, и числа ниже — ровно те же; со шкалой v8.2 (300 000,
 * SPHERE_SCALES_V82_ENABLED) каждая ступень вырастает втрое вместе с ней.
 */
// `?.` — модуль грузится и там, где шкал нет (тесты экранов с заглушкой otherScores).
const rankAt = (atScale100k: number): number => Math.round((atScale100k * (SPHERE_FULL_AT?.contrib || 100000)) / 100000);
export const RANKS: Rank[] = [
    { at: 0, name: "Начало" },
    { at: rankAt(1000), name: "Участник" },
    { at: rankAt(5000), name: "Помощник" },
    { at: rankAt(15000), name: "Наставник" },
    { at: rankAt(35000), name: "Организатор" },
    { at: rankAt(65000), name: "Опора проекта" },
    { at: rankAt(100000), name: "Незаменим" },
];
export const RANKS_EN: Rank[] = [
    { at: 0, name: "Getting started" },
    { at: rankAt(1000), name: "Participant" },
    { at: rankAt(5000), name: "Helper" },
    { at: rankAt(15000), name: "Mentor" },
    { at: rankAt(35000), name: "Organiser" },
    { at: rankAt(65000), name: "Mainstay" },
    { at: rankAt(100000), name: "Indispensable" },
];

/** @param points — все баллы вклада, а не число людей. */
export const rankOf = (points: number, en = false): Rank => {
    const list = en ? RANKS_EN : RANKS;
    let r = list[0];
    for (const x of list) if (points >= x.at) r = x;
    return r;
};
export const nextRank = (points: number, en = false): Rank | null => {
    const list = en ? RANKS_EN : RANKS;
    for (const x of list) if (points < x.at) return x;
    return null;
};

/** Доля пути до следующего звания — ею наполняется сфера. */
export const rankProgress = (points: number): number => {
    const cur = rankOf(points);
    const nxt = nextRank(points);
    if (!nxt) return 1;
    return Math.min(1, Math.max(0, (points - cur.at) / (nxt.at - cur.at)));
};

/**
 * Огоньки: один — один человек, десять в круге, дальше новый круг. ЛЮДИ, а не
 * баллы — это второе число, которое владелец просил оставить для мотивации, и
 * единственное место, где число людей по-прежнему на что-то влияет.
 */
export const DOTS_PER_LAP = 10;
export const dotsLit = (people: number): number =>
    people > 0 && people % DOTS_PER_LAP === 0 ? DOTS_PER_LAP : people % DOTS_PER_LAP;
export const lapsDone = (people: number): number =>
    people <= 0 ? 0 : Math.ceil(people / DOTS_PER_LAP) - 1;
